import React, { useEffect, useState } from "react";
import { FaBuilding, FaCheck, FaClock, FaEye, FaSearch, FaTimes, FaUser } from "react-icons/fa";
import CompactActionBar from "../components/CompactActionBar";
import { useHiddenMode } from "../contexts/HiddenModeContext";
import "../css/standard-components.css";
import { usePageTitle } from "../hooks/usePageTitle";
import bookingService, { Booking } from "../services/bookingService";
import { Project, projectService } from "../services/projectService";
import { formatMainDBAmount, formatSystemsDBAmount, formatTotalAmount } from "../utils/formatAmount";
import { formatUnitDisplay } from "../utils/unitSearch";

const BookingAuthorize: React.FC = () => {
  usePageTitle();
  const { isHiddenModeActive } = useHiddenMode();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    projectId: "",
    search: "",
    page: 1,
    limit: 10,
  });

  // Selection
  const [selectedBookings, setSelectedBookings] = useState<Set<number>>(
    new Set()
  );
  const [selectAll, setSelectAll] = useState(false);

  // Removed authorization modal state (direct approve flow)

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // Edit history modal
  const [editHistoryModal, setEditHistoryModal] = useState<{
    isOpen: boolean;
    bookingId: number | null;
    history: any[];
    loading: boolean;
  }>({
    isOpen: false,
    bookingId: null,
    history: [],
    loading: false,
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadPendingBookings();
  }, [filters]);

  const loadInitialData = async () => {
    try {
      const projectsData = await projectService.getProjects();
      setProjects(projectsData);
    } catch (err) {
      setError("Failed to load initial data");
    }
  };

  const loadPendingBookings = async () => {
    try {
      setLoading(true);
      setError(""); // Clear any previous errors
      const response = await bookingService.getPendingBookings(filters);
      setBookings(response.bookings || []);
      setPagination(
        response.pagination || { page: 1, limit: 10, total: 0, totalPages: 0 }
      );
      setSelectedBookings(new Set());
      setSelectAll(false);
    } catch (err: any) {
      console.error("Error loading pending bookings:", err);
      console.error("Error response:", err.response?.data);
      setError(
        err.response?.data?.message || "Failed to load pending bookings"
      );
      setBookings([]); // Clear bookings on error
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string | number) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page:
        key !== "page"
          ? 1
          : typeof value === "number"
          ? value
          : parseInt(value),
    }));
  };

  const handleSelectBooking = (bookingId: number) => {
    const newSelected = new Set(selectedBookings);
    if (newSelected.has(bookingId)) {
      newSelected.delete(bookingId);
    } else {
      newSelected.add(bookingId);
    }
    setSelectedBookings(newSelected);
    setSelectAll(newSelected.size === bookings.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedBookings(new Set());
    } else {
      setSelectedBookings(new Set(bookings.map((b) => b.id)));
    }
    setSelectAll(!selectAll);
  };

  const handleApproveSelected = async () => {
    if (selectedBookings.size === 0) {
      setError("Please select at least one booking");
      return;
    }

    // Simple lightweight confirmation (no modal UI)
    if (!window.confirm(`Approve ${selectedBookings.size} booking(s)?`)) {
      return;
    }

    try {
      const response = await bookingService.authorizeBookings({
        bookingIds: Array.from(selectedBookings),
        action: "approve",
      });

      const successCount = response.results.filter((r: any) => r.success).length;
      const failCount = response.results.filter((r: any) => !r.success).length;

      if (successCount > 0) {
        setSuccess(`${successCount} booking(s) approved successfully`);
        if (failCount > 0) {
          setError(`${failCount} booking(s) failed to approve`);
        }
      } else {
        setError("Failed to approve selected bookings");
      }
      loadPendingBookings();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to approve bookings");
    }
  };

  const handleApproveIndividual = async (bookingId: number) => {
    if (!window.confirm('Approve this booking?')) {
      return;
    }

    try {
      const response = await bookingService.authorizeBookings({
        bookingIds: [bookingId],
        action: "approve",
      });

      if (response.results[0]?.success) {
        setSuccess('Booking approved successfully');
        loadPendingBookings();
      } else {
        setError(response.results[0]?.message || 'Failed to approve booking');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to approve booking');
    }
  };

  const handleViewEditHistory = async (bookingId: number) => {
    setEditHistoryModal({
      isOpen: true,
      bookingId,
      history: [],
      loading: true,
    });

    try {
      const response = await bookingService.getBookingEditHistory(bookingId);
      setEditHistoryModal((prev) => ({
        ...prev,
        history: response.data || [],
        loading: false,
      }));
    } catch (err: any) {
      console.error("Error fetching edit history:", err);
      setEditHistoryModal((prev) => ({
        ...prev,
        history: [],
        loading: false,
      }));
    }
  };

  const formatCurrency = (amount: number): string => {
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString("en-IN");
  };

  return (
    <div className="page-container" style={{ background: '#fff' }}>
      {/* Compact Action Bar */}
      <CompactActionBar
        leftActions={
          <>
            <button
              className={`filter-toggle-btn ${showFilters ? 'active' : ''}`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaSearch /> Filters
            </button>

            <div className="cab-filter-group">
              <span>Project:</span>
              <select
                className="cab-select"
                value={filters.projectId}
                onChange={(e) => handleFilterChange("projectId", e.target.value)}
              >
                <option value="">All Projects</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="cab-filter-group">
              <span>Show:</span>
              <select
                className="cab-select"
                style={{ minWidth: "80px" }}
                value={filters.limit}
                onChange={(e) =>
                  handleFilterChange("limit", parseInt(e.target.value))
                }
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </>
        }
        rightActions={
          <>
            {selectedBookings.size > 0 && (
              <button
                onClick={handleApproveSelected}
                className="btn btn-success"
                style={{ padding: '8px 16px', fontSize: '13px' }}
              >
                <FaCheck /> Approve ({selectedBookings.size})
              </button>
            )}
          </>
        }
      />

      {error && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#dc2626",
            padding: "12px 20px",
            margin: "0",
            position: "relative",
          }}
        >
          {error}
          <button
            onClick={() => setError("")}
            style={{
              position: "absolute",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#dc2626",
            }}
          >
            ×
          </button>
        </div>
      )}

      {success && (
        <div
          style={{
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            color: "#16a34a",
            padding: "12px 20px",
            margin: "0",
            position: "relative",
          }}
        >
          {success}
          <button
            onClick={() => setSuccess("")}
            style={{
              position: "absolute",
              right: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#16a34a",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Collapsible Filters */}
      {showFilters && (
        <div style={{ padding: '16px 20px', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: '1', minWidth: '250px' }}>
              <input
                type="text"
                placeholder="Search by customer, unit, booking ID..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                className="form-input"
                style={{ margin: 0 }}
              />
            </div>
            
            <button
              onClick={() => {
                handleFilterChange("search", "");
                handleFilterChange("projectId", "");
              }}
              className="btn btn-secondary"
              style={{ padding: '8px 16px', fontSize: '13px' }}
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Table Section */}
      <div style={{ background: '#fff', margin: 0, padding: 0 }}>
        {loading ? (
          <div
            style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}
          >
            Loading pending bookings...
          </div>
        ) : bookings.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px", color: "#6b7280" }}>
            <FaClock style={{ fontSize: "48px", color: "#d1d5db", marginBottom: "16px" }} />
            <h3 style={{ margin: "0 0 8px 0", color: "#374151", fontSize: "18px" }}>No Pending Bookings</h3>
            <p style={{ margin: 0, fontSize: "14px" }}>All bookings are up to date</p>
          </div>
        ) : (
          <>
            <table className="standard-table" style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th style={{ width: '50px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th style={{ width: '80px', textAlign: 'center' }}>Sr. No</th>
                  <th>Project</th>
                  <th>Unit No</th>
                  <th>Customer Name</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th>Booking Date</th>
                  <th>Entry/Updated</th>
                  <th style={{ width: '80px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking, index) => {
                  const primaryApplicant = booking.applicants.find((a) => a.isPrimary) || booking.applicants[0];
                  return (
                    <tr key={booking.id}>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedBookings.has(booking.id)}
                          onChange={(e) => {
                            const newSelected = new Set(selectedBookings);
                            if (e.target.checked) {
                              newSelected.add(booking.id);
                            } else {
                              newSelected.delete(booking.id);
                            }
                            setSelectedBookings(newSelected);
                            setSelectAll(newSelected.size === bookings.length);
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td style={{ fontWeight: '600', color: '#4f46e5', textAlign: 'center' }}>{index + 1}</td>
                      <td>{booking.unit?.project?.name || booking.project?.name || 'N/A'}</td>
                      <td style={{ fontWeight: '500' }}>{formatUnitDisplay(booking.unit.wing, booking.unit.unitNo)}</td>
                      <td style={{ fontWeight: '500', color: '#374151' }}>
                        {primaryApplicant?.firstName} {primaryApplicant?.surname}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap', fontWeight: '500', color: '#059669' }}>
                        {formatMainDBAmount(booking.saleDeedAmount || booking.totalAmount || 0)}
                      </td>
                      <td>{new Date(booking.bookingDate).toLocaleDateString('en-IN')}</td>
                      <td style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                        {new Date(booking.updatedAt || booking.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}{' '}
                        {new Date(booking.updatedAt || booking.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                          <button
                            onClick={() => handleApproveIndividual(booking.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#10b981',
                              fontSize: '14px',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                            title="Approve Booking"
                          >
                            <FaCheck />
                          </button>
                          <button
                            onClick={() => handleViewEditHistory(booking.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#4f46e5',
                              fontSize: '14px',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center',
                            }}
                            title="View Edit History"
                          >
                            <FaEye />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '16px 20px',
                borderTop: '1px solid #e5e7eb',
                background: '#f9fafb'
              }}>
                <button
                  onClick={() => handleFilterChange("page", pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="btn btn-secondary"
                  style={{ padding: '8px 16px', fontSize: '13px' }}
                >
                  Previous
                </button>

                <span style={{ fontSize: '14px', color: '#6b7280' }}>
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </span>

                <button
                  onClick={() => handleFilterChange("page", pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="btn btn-secondary"
                  style={{ padding: '8px 16px', fontSize: '13px' }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit History Modal */}
      {editHistoryModal.isOpen && (
        <div className="modal-overlay" onClick={() => setEditHistoryModal({ isOpen: false, bookingId: null, history: [], loading: false })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h3>Booking Edit History</h3>
              <button
                className="close-button"
                onClick={() => setEditHistoryModal({ isOpen: false, bookingId: null, history: [], loading: false })}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-body" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {editHistoryModal.loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  Loading edit history...
                </div>
              ) : editHistoryModal.history.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  No edit history found for this booking
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {editHistoryModal.history.map((record, index) => (
                    <div
                      key={record.id}
                      style={{
                        padding: '16px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        background: '#f9fafb',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div>
                          <span style={{ fontWeight: 600, color: '#4f46e5', fontSize: '14px' }}>
                            Edit #{editHistoryModal.history.length - index}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#6b7280' }}>
                          {new Date(record.editedAt).toLocaleString('en-IN')}
                        </div>
                      </div>
                      
                      <div style={{ marginBottom: '8px' }}>
                        <strong style={{ fontSize: '13px', color: '#374151' }}>Reason:</strong>
                        <div style={{ fontSize: '14px', color: '#059669', marginTop: '4px', fontWeight: 500 }}>
                          {record.editReason}
                        </div>
                      </div>

                      {record.editDetails && (
                        <div style={{ marginBottom: '8px' }}>
                          <strong style={{ fontSize: '13px', color: '#374151' }}>Details:</strong>
                          <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                            {record.editDetails}
                          </div>
                        </div>
                      )}

                      <div style={{ marginBottom: '8px' }}>
                        <strong style={{ fontSize: '13px', color: '#374151' }}>Changed Fields:</strong>
                        <div style={{ marginTop: '8px', fontSize: '13px' }}>
                          {Object.keys(record.changedFields).length === 0 ? (
                            <span style={{ color: '#6b7280' }}>No specific fields tracked</span>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {Object.entries(record.changedFields).map(([field, value]: [string, any]) => {
                                const hasOldValue = value.old !== undefined && value.old !== null;
                                const displayField = field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                                
                                return (
                                  <div key={field} style={{ 
                                    padding: '10px 12px', 
                                    background: 'white', 
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb'
                                  }}>
                                    <div style={{ fontWeight: 600, color: '#4f46e5', marginBottom: '6px' }}>
                                      {displayField}
                                    </div>
                                    
                                    {hasOldValue ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          <span style={{ 
                                            fontSize: '11px', 
                                            fontWeight: 600, 
                                            color: '#6b7280',
                                            minWidth: '40px'
                                          }}>
                                            OLD:
                                          </span>
                                          <span style={{ 
                                            color: '#dc2626', 
                                            fontWeight: 500,
                                            textDecoration: 'line-through',
                                            flex: 1
                                          }}>
                                            {typeof value.old === 'object' 
                                              ? JSON.stringify(value.old, null, 2) 
                                              : value.old?.toLocaleString?.() || value.old}
                                          </span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                          <span style={{ 
                                            fontSize: '11px', 
                                            fontWeight: 600, 
                                            color: '#6b7280',
                                            minWidth: '40px'
                                          }}>
                                            NEW:
                                          </span>
                                          <span style={{ 
                                            color: '#059669', 
                                            fontWeight: 600,
                                            flex: 1
                                          }}>
                                            {typeof value.new === 'object' 
                                              ? JSON.stringify(value.new, null, 2) 
                                              : value.new?.toLocaleString?.() || value.new}
                                          </span>
                                        </div>
                                      </div>
                                    ) : (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ 
                                          fontSize: '11px', 
                                          fontWeight: 600, 
                                          color: '#6b7280',
                                          minWidth: '40px'
                                        }}>
                                          VALUE:
                                        </span>
                                        <span style={{ color: '#059669', fontWeight: 600, flex: 1 }}>
                                          {typeof value === 'object' && value.new !== undefined
                                            ? (typeof value.new === 'object' 
                                                ? JSON.stringify(value.new, null, 2) 
                                                : value.new?.toLocaleString?.() || value.new)
                                            : (typeof value === 'object' 
                                                ? JSON.stringify(value, null, 2) 
                                                : value?.toLocaleString?.() || value)}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>

                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', fontStyle: 'italic' }}>
                        Edited by: {record.editedBy}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingAuthorize;
